import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Layout from "../components/Layout";
import Steps, { stepIndexOf } from "../components/Steps";
import docusignService from "../services/docusign.service";

// #11 - Landing page after signing. The agent gets here by a full-page redirect
// from DocuSign (real) or the mock page, so the wizard's in-memory state is gone.
// We confirm the envelope status with the server and, if signing is complete,
// rehydrate the wizard from the stored payload and drop the agent on Review (the
// step right after DocuSign). If it isn't complete (e.g. they cancelled, or
// followed a reminder link), we offer to resume signing.
const SigningReturn = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true });
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    docusignService
      .getStatus(id)
      .then((res) => setState({ loading: false, ...res }))
      .catch(() =>
        setState({ loading: false, error: "We couldn't confirm your signature." })
      );
  }, [id]);

  const resumeSigning = () => {
    setResuming(true);
    docusignService
      .startSigning({ unsignedId: id, ...(state.payload || {}) })
      .then(({ signingUrl }) => {
        window.location.href = signingUrl;
      })
      .catch(() => setResuming(false));
  };

  if (state.loading) {
    return (
      <Layout>
        <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
          <Stack spacing={2} alignItems="center" sx={{ mt: 6 }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Confirming your signature...
            </Typography>
          </Stack>
        </Box>
      </Layout>
    );
  }

  // Signed: rehydrate the wizard and continue at Review.
  if (state.completed) {
    return (
      <Layout>
        <Box sx={{ padding: 4 }}>
          <Steps
            initialData={state.payload}
            initialStep={stepIndexOf(state.payload, "Review")}
            unsignedId={id}
          />
        </Box>
      </Layout>
    );
  }

  // Not signed (cancelled, still pending, or a reminder link): offer to resume.
  return (
    <Layout>
      <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
        <Paper sx={{ p: 4, maxWidth: 560, textAlign: "center" }} elevation={2}>
          <Stack spacing={2} alignItems="center">
            <Alert severity="warning" sx={{ width: "100%" }}>
              {state.error || "Your contract isn't signed yet."}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              You need to sign your W-9 and EFT forms before you can submit your
              contract request.
            </Typography>
            <Button
              variant="contained"
              size="large"
              disabled={resuming}
              onClick={resumeSigning}
              startIcon={resuming ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {resuming ? "Taking you to DocuSign..." : "Resume signing"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
};

export default SigningReturn;
